const NEWLY_ADDED_CLASS = 'board-newly-added';

/** User extension pins and AI pins that should flash a green border when created. */
export const NEWLY_ADDED_PIN_TYPES = new Set([
  'pin-card',
  'suggest-pin',
  'conversation-pin',
]);

export const newlyAddedShapeIds = new Set();

function shapeDomId(el) {
  return el?.getAttribute?.('data-shape-id') || '';
}

function findShapeEl(container, shapeId) {
  if (!container || !shapeId) return null;
  return container.querySelector(
    `.tl-shape[data-shape-id="${CSS.escape(shapeId)}"]:not(.tl-shape-background)`
  );
}

function syncNewlyAddedDom(container) {
  if (!container) return;

  container.querySelectorAll(`.tl-shape.${NEWLY_ADDED_CLASS}`).forEach(el => {
    if (!newlyAddedShapeIds.has(shapeDomId(el))) {
      el.classList.remove(NEWLY_ADDED_CLASS);
    }
  });

  for (const id of newlyAddedShapeIds) {
    findShapeEl(container, id)?.classList.add(NEWLY_ADDED_CLASS);
  }
}

export function markShapeNewlyAdded(shapeId, container = null) {
  const id = String(shapeId || '').trim();
  if (!id) return;
  newlyAddedShapeIds.add(id);
  if (!container) return;
  requestAnimationFrame(() => syncNewlyAddedDom(container));
}

export function clearNewlyAddedShapes(container) {
  newlyAddedShapeIds.clear();
  container
    ?.querySelectorAll(`.tl-shape.${NEWLY_ADDED_CLASS}`)
    .forEach(el => el.classList.remove(NEWLY_ADDED_CLASS));
}

/**
 * Mark user extension pins and AI pins created after mount with a green border
 * until the user hovers them.
 * Skip while `shouldSkip()` is true (initial board hydrate) so load does not flash.
 *
 * The class is applied on tldraw's shape node, which React may recreate or
 * reset after the create handler runs (culling, height sync, send-to-back).
 * A mutation observer re-applies it until the node is actually on screen.
 */
export function registerNewlyAddedShapeHighlight(editor, { shouldSkip } = {}) {
  if (!editor) return () => {};

  const container = editor.getContainer();
  let rafId = null;

  const scheduleSync = () => {
    if (rafId != null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      syncNewlyAddedDom(container);
    });
  };

  const unregisterCreate = editor.sideEffects.registerAfterCreateHandler('shape', shape => {
    if (shouldSkip?.()) return;
    if (!shape?.id || !NEWLY_ADDED_PIN_TYPES.has(shape.type)) return;
    newlyAddedShapeIds.add(shape.id);
    scheduleSync();
  });

  const unregisterDelete = editor.sideEffects.registerAfterDeleteHandler('shape', shape => {
    if (!shape?.id || !newlyAddedShapeIds.delete(shape.id)) return;
    scheduleSync();
  });

  const handlePointerOver = event => {
    const shapeEl = event.target?.closest?.('.tl-shape:not(.tl-shape-background)');
    const id = shapeDomId(shapeEl);
    if (!id || !newlyAddedShapeIds.has(id)) return;
    newlyAddedShapeIds.delete(id);
    shapeEl.classList.remove(NEWLY_ADDED_CLASS);
  };

  container?.addEventListener('pointerover', handlePointerOver);

  const unlistenStore = editor.store.listen(() => {
    if (newlyAddedShapeIds.size) scheduleSync();
  });

  const observer = container
    ? new MutationObserver(() => {
      if (newlyAddedShapeIds.size) scheduleSync();
    })
    : null;
  observer?.observe(container, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });

  return () => {
    if (rafId != null) cancelAnimationFrame(rafId);
    unregisterCreate();
    unregisterDelete();
    unlistenStore();
    observer?.disconnect();
    container?.removeEventListener('pointerover', handlePointerOver);
  };
}
